import { useState, useEffect } from 'react';
import { FaRocket, FaStar, FaGraduationCap, FaSchool, FaBook, FaBullseye } from 'react-icons/fa';
import ChaptersPage from './ChaptersPage';
import PracticePage from './PracticePage';

const HomePage = ({ selectedStudent, user, onLogout, onViewChapters, onSchoolDataLoad, onPracticeTest, onSelectSyllabus, onSelectChapter, onStartActivity, initialViewMode = 'learn' }) => {
  const [schoolData, setSchoolData] = useState(null);
  const [loadingSchool, setLoadingSchool] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [viewMode, setViewMode] = useState(initialViewMode); // 'learn' or 'practice'

  // Fetch school data when component loads or student changes
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (!selectedStudent?.school?.id) {
        setLoadingSchool(false);
        return;
      }

      try {
        setLoadingSchool(true);

        // Import listSchools dynamically to avoid circular dependency issues
        const { listSchools } = await import('@dataconnect/generated');

        // Fetch all schools and find the one matching the student's school ID
        const result = await listSchools({ limit: 5000 });

        if (result.data?.schools) {
          const school = result.data.schools.find(s => s.id === selectedStudent.school.id);
          if (school) {
            setSchoolData(school);
            if (onSchoolDataLoad) {
              onSchoolDataLoad(school);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load school information:', err);
      } finally {
        setLoadingSchool(false);
      }
    };

    fetchSchoolData();
  }, [selectedStudent]);

  // Fetch selected config details when selectedConfigId changes
  useEffect(() => {
    const fetchSelectedConfig = async () => {
      if (!selectedStudent?.selectedConfigId) {
        setSelectedConfig(null);
        return;
      }

      try {
        setLoadingConfig(true);
        const { getChapterConfigById } = await import('@dataconnect/generated');
        const result = await getChapterConfigById({ id: selectedStudent.selectedConfigId });
        
        if (result.data?.chapterConfig) {
          setSelectedConfig(result.data.chapterConfig);
        } else {
          console.warn('No config data found in result. Data keys:', Object.keys(result.data || {}));
        }
      } catch (err) {
        console.error('Failed to load selected config:', err);
      } finally {
        setLoadingConfig(false);
      }
    };

    fetchSelectedConfig();
  }, [selectedStudent?.selectedConfigId]);

  if (loadingSchool) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#1b263b] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-cyan-400 text-lg">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        background: viewMode === 'learn' ? 'transparent' : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #bae6fd 50%, #7dd3fc 75%, #38bdf8 100%)'
      }}
    >
      {/* Grid Pattern Overlay - Only show in practice mode to not conflict with ChaptersPage bg */}
      {viewMode === 'practice' && (
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.2) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      )}

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Top Controls Area */}
        <div className="flex justify-center mb-8">
          {/* View Mode Toggle - Centered */}
          <div className="bg-white/50 backdrop-blur-md p-1.5 rounded-2xl border-2 border-blue-200/50 shadow-xl flex items-center space-x-1 h-[58px]">
            <button
              onClick={() => setViewMode('learn')}
              className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl transition-all duration-300 h-full ${
                viewMode === 'learn'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                  : 'text-blue-600 hover:bg-blue-100'
              }`}
            >
              <FaBook className={viewMode === 'learn' ? 'text-white' : 'text-blue-500'} />
              <span className="font-bold">Learn Chapterwise</span>
            </button>
            <button
              onClick={() => setViewMode('practice')}
              className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl transition-all duration-300 h-full ${
                viewMode === 'practice'
                  ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-lg shadow-pink-500/30'
                  : 'text-pink-600 hover:bg-pink-100'
              }`}
            >
              <FaBullseye className={viewMode === 'practice' ? 'text-white' : 'text-pink-500'} />
              <span className="font-bold">Practice Across Chapters</span>
            </button>
          </div>
        </div>

        {/* Dynamic Content Area */}
        <div className="mt-4">
          {viewMode === 'learn' ? (
            <div className="-mt-12"> {/* Adjust for ChaptersPage internal padding */}
              <ChaptersPage 
                selectedStudent={selectedStudent} 
                onLogout={onLogout} 
                onSelectChapter={onSelectChapter} 
                onSchoolDataLoad={onSchoolDataLoad}
              />
            </div>
          ) : (
            <div className="-mt-12"> {/* Adjust for PracticePage internal padding */}
              <PracticePage 
                selectedStudent={selectedStudent} 
                user={user}
                onStartActivity={(activity) => onStartActivity(activity, 'practice')}
                onSelectSyllabus={onSelectSyllabus}
                selectedConfig={selectedConfig}
                loadingConfig={loadingConfig}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
